export type ParticipantType =
  | 'Owner'
  | 'Administrator'
  | 'Participant'
  | 'Viewer';

export type OrganizationAccess = {
  id: string;
  name: string;
  participantType: ParticipantType;
  isLastOwner: boolean;
};

export type ChildResourceAccess = {
  id: string;
  name: string;
  organization: {
    id: string;
    name: string;
  };
  participantType: ParticipantType;
  isLastAdmin: boolean;
};

export type UserResourceAccessResponse = {
  organizations: OrganizationAccess[];
  binders: ChildResourceAccess[];
  tenders: ChildResourceAccess[];
};

export type AssignmentStatus = {
  code: string;
  label: string;
  nativeCode: string;
};

export type AssignmentResponsibility = {
  code: string;
  label: string;
};

export type BinderReference = {
  id: string;
  name: string;
};

export type UserAssignment = {
  domain: string;
  resourceId: string;
  resourceName: string;
  dueDate: string | null;
  dueDateKind: string | null;
  status: AssignmentStatus;
  responsibility: AssignmentResponsibility;
  binder: BinderReference;
};

export type UserAssignmentsResponse = {
  assignments: UserAssignment[];
};

// One organization's assignments from the dictionary the endpoint returns.
export type UserAssignmentsForOrganization = {
  organizationId: string;
  assignments: UserAssignment[];
};

export type UserAssignmentsByOrganizationResponse = {
  byOrganization: UserAssignmentsForOrganization[];
};

// The resource whose assignments are shown. A binder also sends binderId;
// an organization asks for every assignment in that organization.
export type AssignmentsTarget = {
  kind: 'Organization' | 'Binder';
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  participantType?: ParticipantType;
};

export type ResourceOrganization = {
  id: string;
  name: string;
};

export type BinderAccessNode = {
  id: string;
  name: string;
  organization: ResourceOrganization;
  participantType: ParticipantType;
  isLastAdmin: boolean;
};

export type TenderAccessNode = {
  id: string;
  name: string;
  organization: ResourceOrganization;
  participantType: ParticipantType;
  isLastAdmin: boolean;
};

export type OrganizationAccessNode = {
  id: string;
  name: string;
  // False when the user only reaches the organization through a binder or
  // tender inside it, without a role in the organization itself.
  hasDirectAccess: boolean;
  participantType?: ParticipantType;
  isLastOwner: boolean;
  binders: BinderAccessNode[];
  tenders: TenderAccessNode[];
};

// Every binder and tender is grouped under its organization; the groups are
// then split by whether the user participates in the organization itself.
export type UserAccessGroups = {
  direct: OrganizationAccessNode[];
  indirect: OrganizationAccessNode[];
};

export type RemovableResourceKind = 'organization' | 'binder' | 'tender';

export const resourceSelectionKey = (kind: RemovableResourceKind, id: string) =>
  `${kind}:${id}`;

// A binder or tender to remove the user from, with the organization it
// belongs to.
export type ResourceRemoval = {
  id: string;
  organizationId: string;
};

// Binder and tender ids are strings. Organization ids are GUIDs, which JSON
// delivers as strings, so every list is string[].
export type ResourceDeletions = {
  binders: string[];
  tenders: string[];
  organizations: string[];
};

export type RemoveUserFromResourcesResponse = {
  successfulDeletions: ResourceDeletions;
  failedDeletions: ResourceDeletions;
};

const readIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    : [];

export const readResourceDeletions = (value: any): ResourceDeletions => ({
  binders: readIds(value?.binders),
  tenders: readIds(value?.tenders),
  organizations: readIds(value?.organizations)
});

export const deletionCount = (deletions: ResourceDeletions) =>
  deletions.organizations.length +
  deletions.binders.length +
  deletions.tenders.length;
